import { GetStaticPaths, GetStaticProps } from 'next'
import { FC } from 'react'
import { PrefectureRomas, prefectures } from '@lifedot/constants/prefectures'
import { sorts } from '@lifedot/constants/sort'
import { initEnvironment } from '@lifedot/relay'
import { fetchQuery } from 'relay-runtime'
import { prefectureStationListPageQuery } from '@/queries/prefectureStationListPage'
import { ConditionLinksList } from '@/components/ConditionLinksList'
import { StationLinksList } from '@/components/StationLinksList'
import { Hakajimai } from '@/components/Hakajimai'
import { CommonContentsDummy } from '@/contents/CommonContentsDummy'
import { CommonContents } from '@/contents/CommonContents'
import { prefectureStationListPage_indexQuery } from '@/queries/__generated__/prefectureStationListPage_indexQuery.graphql'
import { AddMarginWrapper } from '@lifedot/components/AddMarginWrapper'
import { useQuery } from 'relay-hooks/lib/useQuery'
import { ListPageContextProvider } from '@/hooks/use-list-page'
import { useRouter } from 'next/router'
import { getSearchParamsByQuery } from '@/hooks/use-list-page/dependencies'
import { Faq } from '@/components/Faq'
import { AveragePrice } from '@/components/AveragePrice'
import { router } from '@lifedot/router'
import { ShopLinksList } from '@/components/ShopLinksList/ShopLinksList'

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

export const getStaticProps: GetStaticProps<
  ListPageProps,
  {
    prefecture: string
    station: string
  }
> = async ({ params }) => {
  const prefectureRoma = params?.prefecture ?? ''
  const stationId = Number(params?.station)
  try {
    const { environment, relaySSR }: ReturnType<typeof initEnvironment> =
      initEnvironment()

    const res = await fetchQuery<prefectureStationListPage_indexQuery>(
      environment,
      prefectureStationListPageQuery,
      {
        prefectureRoma,
        stationId,
        from: 0,
        sort: sorts['default']
      },
      {}
    ).toPromise()

    if (res?.station?.mainStation?.stationId) {
      return {
        redirect: {
          statusCode: 301,
          destination: router.list({
            stationId: res.station.mainStation.stationId,
            prefectureRoma
          })
        },
        revalidate: Number(process.env.DEFAULT_REVALIDATE_TERM)
      }
    }

    const [relayData] = await relaySSR.getCache()

    const [cacheString, payload] = relayData
    if (!payload.data || payload.data.cemeteries.total < 1)
      return {
        notFound: true
      }

    return {
      props: {
        prefecture: {
          roma: prefectureRoma,
          name: prefectures[prefectureRoma as PrefectureRomas]
        },
        stationId,
        relayData:
          !relayData || !('json' in payload)
            ? null
            : [[cacheString, payload.json]]
      },
      revalidate: Number(process.env.DEFAULT_REVALIDATE_TERM)
    }
  } catch (e) {
    if (typeof e === 'object' && e !== null && 'res' in e) {
      if (typeof (e as { res?: unknown }).res === 'object')
        console.log((e as { res?: { errors?: unknown } }).res?.errors)
    } else if (e instanceof Error) console.log(e.message)

    return {
      props: {
        prefecture: {
          roma: prefectureRoma,
          name: prefectures[prefectureRoma as PrefectureRomas]
        },
        stationId
      },
      revalidate: 1
    }
  }
}

interface ListPageProps {
  prefecture: {
    roma: string
    name: string
  }
  stationId: number
}

const ListPage: FC<ListPageProps> = (props) => {
  const { query } = useRouter()
  const {
    features,
    cemeteryTypes,
    facilities,
    sectionTypes,
    sects,
    sectionCapacities,
    priceMax,
    priceMin,
    priceRange,
    from,
    sort
  } = getSearchParamsByQuery(query)
  const { data, error } = useQuery<prefectureStationListPage_indexQuery>(
    prefectureStationListPageQuery,
    {
      prefectureRoma: props.prefecture.roma,
      stationId: props.stationId,
      features,
      cemeteryTypes,
      facilities,
      sectionTypes,
      sects,
      sectionCapacities,
      priceMax,
      priceMin,
      priceRange,
      from,
      sort
    }
  )

  if (error || !data) return <CommonContentsDummy />

  return (
    <ListPageContextProvider
      prefecture={props.prefecture}
      pagination={data.cemeteries}
      {...data}
    >
      <CommonContents data={data} prefecture={props.prefecture}>
        <AddMarginWrapper spacing={4}>
          <AveragePrice {...data} />
          <Faq {...data} />
          <ConditionLinksList {...data} />
          <StationLinksList {...data} />
          <ShopLinksList fragmentRef={data.shopCities} />
          <Hakajimai />
        </AddMarginWrapper>
      </CommonContents>
    </ListPageContextProvider>
  )
}

export default ListPage
